import { useState } from "react";
import { Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetDescription,
} from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { QUALITY_FLAGS_LABELS } from "@/constants/wpQualityFlags";

const QUALITY_RULES: Array<{
  rule: string;
  name: string;
  items: Array<{ cond: string; impact: string; flag?: string }>;
}> = [
  {
    rule: "Rule 1",
    name: "Word count",
    items: [
      { cond: "< 300 słów", impact: "−30 pkt", flag: "too_short" },
      { cond: "< 600 słów", impact: "−15 pkt", flag: "thin_content" },
      { cond: "> 1200 słów", impact: "+5 pkt bonus" },
    ],
  },
  {
    rule: "Rule 2",
    name: "Struktura nagłówków",
    items: [
      { cond: "0 H2", impact: "−20 pkt", flag: "no_h2_structure" },
      { cond: "< 2 H2", impact: "−10 pkt", flag: "weak_structure" },
      { cond: "0 H3 przy > 800 słów", impact: "−5 pkt", flag: "flat_structure" },
      { cond: "0 list przy > 600 słów", impact: "−5 pkt", flag: "no_lists" },
    ],
  },
  {
    rule: "Rule 3",
    name: "Keyword stuffing",
    items: [
      { cond: "stuffing score < 40", impact: "−25 pkt", flag: "heavy_stuffing" },
      { cond: "stuffing score < 60", impact: "−15 pkt", flag: "keyword_stuffed" },
    ],
  },
  {
    rule: "Rule 4",
    name: "Linki",
    items: [
      { cond: "0 outbound links", impact: "−5 pkt", flag: "no_outbound_links" },
      { cond: "0 internal links", impact: "−10 pkt", flag: "no_internal_links" },
    ],
  },
  {
    rule: "Rule 5",
    name: "Wiek i świeżość",
    items: [
      { cond: "> 3 lat & > 12 miesięcy bez update", impact: "−10 pkt", flag: "stale_content" },
    ],
  },
  {
    rule: "Rule 6",
    name: "Meta i media",
    items: [
      { cond: "brak / krótkie meta description", impact: "−5 pkt", flag: "no_meta" },
      { cond: "brak featured image", impact: "−3 pkt", flag: "no_featured_image" },
      { cond: "0 obrazków przy > 500 słów", impact: "−3 pkt", flag: "no_images_in_content" },
    ],
  },
];

const MATCH_STATUS_EXPLAIN: Array<{ label: string; desc: string; color: string }> = [
  { label: "Zachowaj", desc: "Wpis jest i wystarczy — Content Engine pomija tego node'a", color: "bg-blue-500/15 text-blue-700 dark:text-blue-400 border-blue-500/30" },
  { label: "Do przebudowy", desc: "Wpis jest ale słaby (quality < 45) — CE UPDATE'uje istniejący URL zamiast tworzyć nowy", color: "bg-orange-500/15 text-orange-700 dark:text-orange-400 border-orange-500/30" },
  { label: "Częściowo", desc: "Częściowy overlap — wymaga ręcznej decyzji (keep / refurbish / ignore)", color: "bg-purple-500/15 text-purple-700 dark:text-purple-400 border-purple-500/30" },
  { label: "Do generowania", desc: "Brak pary w istniejących wpisach — CE tworzy nowy artykuł", color: "bg-green-500/15 text-green-700 dark:text-green-400 border-green-500/30" },
  { label: "Pomiń ręcznie", desc: "User wykluczył tego node'a z dalszej produkcji", color: "bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/30" },
];

const SEO_STATUS_EXPLAIN: Array<{ dot: string; label: string; desc: string }> = [
  { dot: "bg-emerald-500", label: "Dobrze widoczny", desc: "Wpis ma ≥1 frazę w TOP 3 LUB szacowany ruch ≥ 20 wizyt/mies." },
  { dot: "bg-blue-500", label: "Widoczny", desc: "Wpis ma ≥1 frazę w TOP 10 (ale nie w TOP 3)." },
  { dot: "bg-amber-500", label: "Nisko", desc: "Wpis rankuje, ale tylko na pozycjach 11–50." },
  { dot: "bg-zinc-500", label: "Niewidoczny", desc: "Żadna fraza nie wpada do TOP 50 w Google." },
  { dot: "bg-muted-foreground/40", label: "Brak danych", desc: "Senuto sync nie został jeszcze wykonany." },
];

export function ExistingPostsInfoCard() {
  const [open, setOpen] = useState(false);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5 h-8 text-xs border-blue-500/30 hover:bg-blue-500/10"
        >
          <Info className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
          Jak czytać tę zakładkę
        </Button>
      </SheetTrigger>
      <SheetContent
        side="right"
        className="w-full sm:max-w-2xl overflow-hidden flex flex-col p-6"
      >
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Info className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            Istniejące wpisy — jak to czytać
          </SheetTitle>
          <SheetDescription>
            Wszystkie sygnały per wpis: jakość tekstu, SRO, widoczność SEO, status matchu.
          </SheetDescription>
        </SheetHeader>

        <Tabs defaultValue="quality" className="flex-1 overflow-hidden flex flex-col">
          <TabsList className="shrink-0">
            <TabsTrigger value="quality">Jakość 0–100</TabsTrigger>
            <TabsTrigger value="sro">SRO</TabsTrigger>
            <TabsTrigger value="seo">SEO (widoczność)</TabsTrigger>
            <TabsTrigger value="matching">Matching</TabsTrigger>
            <TabsTrigger value="status">Status matchu</TabsTrigger>
          </TabsList>

          {/* === Tab: Jakość === */}
          <TabsContent value="quality" className="flex-1 overflow-y-auto mt-4 space-y-3">
            <div>
              <div className="mb-3 p-3 rounded-md border border-blue-500/30 bg-blue-500/5 text-xs leading-relaxed">
                <p className="font-semibold mb-1.5 text-blue-700 dark:text-blue-400">Krok 4 — 2 fazy</p>
                <p className="text-muted-foreground">
                  <strong className="text-foreground">Faza 1 (heurystyka):</strong> 6 reguł z HTML (poniżej), ~100ms, zero kosztu.
                  Baseline dla WSZYSTKICH wpisów — działa zanim wyekstrahujesz SRO.
                </p>
                <p className="text-muted-foreground mt-1">
                  <strong className="text-foreground">Faza 2 (LLM SRO audit):</strong> gpt-4.1-mini, ~$0.0025/wpis, 4 wymiary SRO (entity anchoring, information gain, humanization, retrieval ready).
                  Nadpisuje <code className="text-[10px]">quality_recommendation</code> TYLKO na wpisach z SRO.
                </p>
              </div>
              <p className="text-xs text-muted-foreground mb-3">
                <strong>Faza 1 (heurystyka)</strong> — zaczynamy od 100 pkt i odejmujemy za wykryte problemy.
                Źródło: <code className="text-[10px]">src/services/wpQualityAssessor.ts</code>.
              </p>

              <div className="rounded border bg-background overflow-hidden">
                <table className="w-full text-xs border-collapse">
                  <thead className="bg-muted/50 border-b">
                    <tr className="[&>th:not(:last-child)]:border-r [&>th]:border-border/60">
                      <th className="text-left px-3 py-2 font-medium">Reguła</th>
                      <th className="text-left px-3 py-2 font-medium">Warunek</th>
                      <th className="text-left px-3 py-2 font-medium">Wpływ</th>
                      <th className="text-left px-3 py-2 font-medium">Flaga</th>
                    </tr>
                  </thead>
                  <tbody>
                    {QUALITY_RULES.map((group) =>
                      group.items.map((item, idx) => (
                        <tr
                          key={`${group.rule}-${idx}`}
                          className="border-b border-border/40 [&>td:not(:last-child)]:border-r [&>td]:border-border/30"
                        >
                          {idx === 0 ? (
                            <td rowSpan={group.items.length} className="px-3 py-2 align-top font-medium">
                              <div>{group.rule}</div>
                              <div className="text-muted-foreground text-[10px] font-normal">
                                {group.name}
                              </div>
                            </td>
                          ) : null}
                          <td className="px-3 py-2 text-muted-foreground">{item.cond}</td>
                          <td
                            className={`px-3 py-2 font-mono ${
                              item.impact.startsWith("+")
                                ? "text-emerald-600 dark:text-emerald-400"
                                : "text-red-600 dark:text-red-400"
                            }`}
                          >
                            {item.impact}
                          </td>
                          <td className="px-3 py-2">
                            {item.flag ? (
                              <Badge variant="destructive" className="text-[10px]">
                                {QUALITY_FLAGS_LABELS[item.flag] || item.flag}
                              </Badge>
                            ) : (
                              <span className="text-muted-foreground/60">—</span>
                            )}
                          </td>
                        </tr>
                      )),
                    )}
                  </tbody>
                </table>
              </div>

              <div className="mt-4 flex items-center gap-2 flex-wrap text-xs">
                <span className="text-muted-foreground font-medium">Rekomendacja końcowa:</span>
                <Badge className="bg-green-500/15 text-green-700 dark:text-green-400 border-green-500/30 border">
                  🟢 ≥ 65 keep
                </Badge>
                <Badge className="bg-yellow-500/15 text-yellow-700 dark:text-yellow-400 border-yellow-500/30 border">
                  🟡 45–64 borderline
                </Badge>
                <Badge className="bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/30 border">
                  🔴 &lt; 45 refurbish
                </Badge>
              </div>
            </div>
          </TabsContent>

          {/* === Tab: SRO === */}
          <TabsContent value="sro" className="flex-1 overflow-y-auto mt-4 space-y-3">
            <p className="text-xs text-muted-foreground">
              Dla każdego wpisu LLM (gpt-4.1-mini, ~$0.0003/wpis) wyciąga z pierwszych 100 słów:
            </p>
            <div className="rounded border bg-muted/20 p-4 space-y-2 text-xs">
              <div><strong className="text-foreground">entity</strong> — główna encja (o czym artykuł)</div>
              <div><strong className="text-foreground">attribute</strong> — wyciągany atrybut (który aspekt encji)</div>
              <div>
                <strong className="text-foreground">query_frame</strong> — intencja pytania:{" "}
                <code className="text-[10px]">definitional / procedural / comparative / causal / evaluative / mixed</code>
              </div>
              <div><strong className="text-foreground">first_paragraph_answer</strong> — zdanie odpowiedzi na pytanie SRO (BLUF)</div>
              <div><strong className="text-foreground">supporting_entities</strong> — encje wspierające (wspomniane w pierwszym akapicie)</div>
            </div>
            <p className="text-xs text-muted-foreground">
              Dane trafiają do <code className="text-[10px]">wp_posts.extracted_*</code> + <code className="text-[10px]">sro_extracted_at</code>.
              Widoczne w podglądzie wpisu (klik wiersz tabeli → tab "SRO").
            </p>
          </TabsContent>

          {/* === Tab: SEO === */}
          <TabsContent value="seo" className="flex-1 overflow-y-auto mt-4 space-y-3">
            <p className="text-xs text-muted-foreground">
              Sygnały z Senuto: pozycje fraz w Google, szacowany ruch, content decay, kanibalizacja.
              Status widoczności w kolumnie SEO:
            </p>
            <div className="space-y-2">
              {SEO_STATUS_EXPLAIN.map((s) => (
                <div key={s.label} className="flex items-start gap-3 text-xs">
                  <div className="flex items-center gap-1.5 shrink-0 min-w-[140px]">
                    <span className={`w-2.5 h-2.5 rounded-full ${s.dot} shrink-0`} />
                    <span className="font-semibold">{s.label}</span>
                  </div>
                  <span className="text-muted-foreground">{s.desc}</span>
                </div>
              ))}
            </div>

            <div className="pt-3 border-t space-y-2 text-xs">
              <p className="font-semibold">Alerty SEO:</p>
              <div className="flex items-start gap-3">
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/15 text-red-600 dark:text-red-400 font-medium border border-red-500/20 shrink-0 mt-0.5">
                  ⚠ decay
                </span>
                <span className="text-muted-foreground">
                  Content decay — wpis utracił ≥3 frazy w SERP w ostatnim okresie. Traci relewancję.
                </span>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-500/15 text-orange-600 dark:text-orange-400 font-medium border border-orange-500/20 shrink-0 mt-0.5">
                  ⚠ kanibalizacja
                </span>
                <span className="text-muted-foreground">
                  Inny URL z tej samej domeny rankuje na tę samą frazę — konflikt wewnętrzny osłabia obie strony.
                </span>
              </div>
            </div>
          </TabsContent>

          {/* === Tab: Matching === */}
          <TabsContent value="matching" className="flex-1 overflow-y-auto mt-4 space-y-3">
            <p className="text-xs text-muted-foreground">
              3-poziomowy matcher node (z mapy) ↔ wpis WP:
            </p>
            <ol className="space-y-3 text-xs">
              <li className="border-l-2 border-blue-500/40 pl-3">
                <p className="font-semibold">L1 — Hash exact <span className="text-muted-foreground font-normal">(darmowe, &lt; 1ms)</span></p>
                <p className="text-muted-foreground">
                  Normalizacja canonical_question nody i tytułu wpisu → hash SHA.
                  Exact match = 100% pewność że to ten sam temat.
                </p>
              </li>
              <li className="border-l-2 border-violet-500/40 pl-3">
                <p className="font-semibold">L2 — Asymmetric containment <span className="text-muted-foreground font-normal">(darmowe, ~ms)</span></p>
                <p className="text-muted-foreground">
                  <code className="text-[10px]">containment = |tokensNody ∩ tokensWpisu| / |tokensNody|</code>
                  — ile % tokenów pytania nody znajduje się w tytule+pierwszych 100 słowach wpisu.
                  Plus entity check i query_frame check. Progi tunowalne w Ustawienia → Matcher.
                </p>
              </li>
              <li className="border-l-2 border-amber-500/40 pl-3">
                <p className="font-semibold">L3 — LLM wp_matching <span className="text-muted-foreground font-normal">(tylko "Matching głęboki")</span></p>
                <p className="text-muted-foreground">
                  Dla par borderline — OpenAI gpt-4.1-mini rozstrzyga
                  <code className="text-[10px] mx-1">same / partial / different</code>
                  na podstawie SRO (entity + attribute + query_frame + first_paragraph_answer).
                  Wymaga wcześniejszej ekstrakcji SRO.
                </p>
              </li>
            </ol>
          </TabsContent>

          {/* === Tab: Status matchu === */}
          <TabsContent value="status" className="flex-1 overflow-y-auto mt-4 space-y-2">
            <p className="text-xs text-muted-foreground mb-3">
              Każdy node dostaje jeden z 5 statusów — decyduje co zrobi Content Engine:
            </p>
            <div className="space-y-2">
              {MATCH_STATUS_EXPLAIN.map((s) => (
                <div key={s.label} className="flex items-start gap-3 text-xs">
                  <Badge className={`${s.color} border text-[10px] shrink-0 min-w-[130px] justify-center`}>
                    {s.label}
                  </Badge>
                  <span className="text-muted-foreground">{s.desc}</span>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}

